/**
 * Platform detection utilities for audio and other cross-browser concerns
 */

export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || '';
  const isIOS = /iP(ad|hone|od)/.test(userAgent);
  const isIPadOS = userAgent.includes('Mac') && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1;

  return isIOS || isIPadOS;
};

/**
 * Detect Safari browser (includes iOS Safari)
 */
export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  return /^((?!chrome|android).)*safari/i.test(userAgent);
};

/**
 * Check if we're on a mobile device
 */
export const isMobile = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Check if HTMLMediaElement.volume is settable
 * iOS Safari doesn't allow programmatic volume control on media elements
 */
export const canSetMediaElementVolume = (): boolean => {
  return !isIOSDevice();
};

/**
 * Recommend whether to use HTML5 or Web Audio for music playback
 * iOS Safari has partial support for HTMLMediaElement.volume.
 * Prefer Web Audio on iOS so in-app music volume sliders remain functional.
 */
export const shouldUseWebAudioForMusic = (): boolean => {
  return isIOSDevice();
};

/**
 * Platform capabilities summary for audio
 */
export const getAudioPlatformInfo = () => ({
  isIOS: isIOSDevice(),
  isSafari: isSafari(),
  isMobile: isMobile(),
  canSetMediaElementVolume: canSetMediaElementVolume(),
  shouldUseWebAudioForMusic: shouldUseWebAudioForMusic(),
});
